import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useInitReport } from '../../hooks/report/useInitReport';
import { useFileUpload } from '../../hooks/report/useFileUpload';
import { useReportState } from '../../hooks/report/useReportState';
import { useUserTasks } from '../../hooks/report/useUserTasks';
import { saveTaskSelection, getTaskSelection } from '../../utils/taskIdValidator';
import FileUploader from './FileUploader';
import InsufficientBalanceError from './InsufficientBalanceError';
import ConfirmDialog from '../ConfirmDialog';
import { useTranslation } from 'react-i18next';
import './FileUploadStep.css';

const FileUploadStep: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { t, i18n } = useTranslation('create');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const { initTask, insufficientBalanceError } = useInitReport();
  const { uploadExcelFile } = useFileUpload();
  const {
    state: { taskId, isLoading, error, currentStep },
    resetState,
    setTaskId
  } = useReportState();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const [isPro, setIsPro] = useState<boolean>(false);
  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);
  
  // Обработчик загрузки файла
  const handleFileChange = (file: File | null) => {
    setSelectedFile(file);
  };
  
  // Обработчик отправки формы
  const handleSubmitFile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      console.error('Не выбран файл для отправки');
      return;
    }
    
    try {
      setLocalLoading(true);
      
      // Сначала сбрасываем состояние
      resetState();
      console.log('Состояние сброшено, создаем новую задачу');
      
      // Инициализируем задачу
      console.log('Вызов initTask с типом отчета "financial", isPro:', isPro);
      await initTask('financial', isPro);
      
      // Получаем актуальный taskId из localStorage после инициализации
      const { taskId: currentTaskId } = getTaskSelection();
      console.log('initTask выполнен успешно, новый taskId из getTaskSelection():', currentTaskId);
      
      // Проверяем, что задача создана успешно
      if (!currentTaskId) {
        console.error('Ошибка: taskId не найден в localStorage после инициализации задачи');
        setLocalLoading(false);
        return;
      }
      
      // Сохраняем дополнительную информацию о задаче
      saveTaskSelection(currentTaskId);
      console.log(`handleSubmitFile - сохранен taskId в localStorage после инициализации: ${currentTaskId}`);
      
      // Проверяем, что taskId сохранен
      const { taskId: savedTaskId } = getTaskSelection();
      console.log(`handleSubmitFile - проверка taskId после сохранения: ${savedTaskId}`);
      
      // Загружаем файл
      console.log('Начинаем загрузку файла...');
      await uploadExcelFile(selectedFile);
      console.log('Файл успешно загружен');
      
      // Переходим на шаг аналитики
      console.log('Переходим на шаг аналитики');
      console.log('Текущий taskId:', currentTaskId);
      
      // Устанавливаем taskId в состояние перед переходом
      setTaskId(currentTaskId);
      
      // Добавляем небольшую задержку перед переходом
      setTimeout(() => {
        console.log('Выполняем переход на страницу аналитики');
        console.log('Переходим на шаг аналитики с новой задачей:', currentTaskId);
        navigate(`/create-report/analytics`, { state: { taskId: currentTaskId } });
      }, 1000);
    } catch (error) {
      console.error('Ошибка при инициализации задачи или загрузке файла:', error);
    } finally {
      setLocalLoading(false);
    }
  };
  
  // Добавляем хук для получения задач пользователя
  const { tasks, isLoading: tasksLoading, error: tasksError, deleteTask } = useUserTasks();
  
  // Обработчик выбора существующей задачи
  const handleTaskSelect = (taskId: string) => {
    console.log(`handleTaskSelect - выбрана задача с ID: ${taskId}`);
    
    // Сначала сбрасываем состояние
    resetState();
    
    // Затем сохраняем taskId и дополнительную информацию в localStorage
    saveTaskSelection(taskId);
    console.log(`handleTaskSelect - сохранен taskId в localStorage после сброса состояния: ${taskId}`);
    
    // Проверяем, что taskId сохранен
    const { taskId: savedTaskId } = getTaskSelection();
    console.log(`handleTaskSelect - проверка taskId после сохранения: ${savedTaskId}`);
    
    // Устанавливаем taskId в состояние перед переходом
    setTaskId(taskId);
    
    // Переходим на шаг аналитики с передачей taskId в state
    console.log('Переходим на шаг аналитики с существующей задачей:', taskId);
    navigate(`/create-report/analytics`, { state: { taskId } });
  };

  const handleTaskDelete = (e: React.MouseEvent, taskId: string) => {
    e.stopPropagation();
    setDeleteTaskId(taskId);
  };

  const confirmDeleteTask = async () => {
    if (!deleteTaskId) return;
    try {
      await deleteTask(deleteTaskId);
    } catch (err) {
      console.error('Ошибка при удалении задачи:', err);
    } finally {
      setDeleteTaskId(null);
    }
  };

  return (
    <div className="file-upload-step">
      {currentStep === 'insufficient_balance' && insufficientBalanceError ? (
        <InsufficientBalanceError
          balance={insufficientBalanceError.balance}
          requiredBalance={insufficientBalanceError.requiredBalance}
        />
      ) : (
        <>
          <div className="pro-mode-toggle">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={isPro}
                onChange={(e) => setIsPro(e.target.checked)}
              />
              <span className="toggle-slider" />
            </label>
            <div className="pro-mode-info">
              <div className="pro-mode-label">
                <span className="pro-badge">PRO</span>
                <span>{t('upload_step.pro_label')}</span>
              </div>
              <p className="pro-description">
                {t('upload_step.pro_description')}
              </p>
            </div>
          </div>
          <FileUploader
            onFileSelect={handleFileChange}
            onSubmit={handleSubmitFile}
            error={error}
          />
        </>
      )}
      
      {(isLoading || localLoading) && (
        <div className="loading-indicator">
          <p>{t('upload_step.loading_file')}</p>
        </div>
      )}
      
      {/* Секция с незавершенными задачами */}
      {tasks.length > 0 && (
        <div className="unfinished-tasks-section">
          <div className="section-header">
            <h3>{t('upload_step.tasks_title')}</h3>
            <p>{t('upload_step.tasks_subtitle')}</p>
          </div>

          {tasksLoading ? (
            <div className="loading-indicator">
              <p>{t('upload_step.tasks_loading')}</p>
            </div>
          ) : (
            <div className="tasks-grid">
              {tasks.map((task) => (
                <div 
                  key={task.task_id} 
                  className="task-card"
                  onClick={() => handleTaskSelect(task.task_id)}
                >
                  <div className="task-card-header">
                    <div className="file-icon">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 2V8H20" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 13H8" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 17H8" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 9H9H8" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="task-title" title={task.file_name}>{task.file_name}</div>
                  </div>
                  
                  <div className="task-card-content">
                    <div className="task-meta">
                      <div className="task-date">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{new Date(task.created_at).toLocaleString(i18n.language === 'en' ? 'en-US' : 'ru-RU')}</span>
                      </div>
                      <div className="task-stats">
                        <div className="stat-item">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16 4H18C18.5304 4 19.0391 4.21071 19.4142 4.58579C19.7893 4.96086 20 5.46957 20 6V20C20 20.5304 19.7893 21.0391 19.4142 21.4142C19.0391 21.7893 18.5304 22 18 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M15 2H9C8.44772 2 8 2.44772 8 3V5C8 5.55228 8.44772 6 9 6H15C15.5523 6 16 5.55228 16 5V3C16 2.44772 15.5523 2 15 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>{t('upload_step.reports_count', { count: task.reports_count })}</span>
                        </div>
                        <div className="stat-item">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M3.05 11C3.27151 8.68261 4.51919 6.56428 6.48114 5.13446C8.44309 3.70464 10.9422 3.07123 13.3551 3.37074C15.768 3.67025 17.9331 4.87574 19.3936 6.75205C20.854 8.62837 21.4957 11.0222 21.1759 13.4078C20.8561 15.7935 19.6058 17.9454 17.7191 19.3911C15.8324 20.8368 13.4387 21.4607 11.0548 21.1231C8.67096 20.7854 6.51162 19.5192 5.06968 17.6235C3.62775 15.7279 3.00141 13.3176 3.33 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>{t('upload_step.pending_count', { count: task.pending_reports })}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Отображаем карточки отчетов */}
                    {task.report_cards && task.report_cards.length > 0 && (
                      <div className="planned-reports">
                        <div className="planned-reports-header">{t('upload_step.planned_reports')}</div>
                        <div className="planned-reports-list">
                          {task.report_cards.map((card, idx) => (
                            <div key={idx} className="planned-report-item">
                              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 11L12 14L22 4" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <span>{card.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="task-card-footer">
                    <button className="delete-task-button" onClick={(e) => handleTaskDelete(e, task.task_id)} title={t('upload_step.delete_task')}>
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button className="continue-button">
                      <span>{t('upload_step.continue')}</span>
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 12H19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 5L19 12L12 19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {tasksError && (
            <div className="error-message">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p>{tasksError}</p>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={deleteTaskId !== null}
        title={t('upload_step.delete_task_title')}
        message={t('upload_step.delete_task_confirm')}
        onConfirm={confirmDeleteTask}
        onCancel={() => setDeleteTaskId(null)}
      />
    </div>
  );
};

export default FileUploadStep;
